// @react
import React from 'react'
// @types
import { GetUserQuery, GetUserQueryVariables } from 'lib/graphql/graphqlTypes'
// @libraries
import Helper from 'utils/Helper.class'
import strings from 'lib/constants/strings'
import parse from 'html-react-parser'
import { Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
// @graphql
import { useQuery } from '@apollo/client'
import { GET_USER } from 'graphql/queries/user.query'
// @components
import Spacer from '@bit/atd.web.spacer'
import Grid from '@bit/atd.web.grid'
import Box from '@bit/atd.web.box'
import Button from '@bit/atd.web.button'
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
// @module components
import QueryError from 'modules/common/components/Errors/QueryError'
import { RouteNamesExternal } from 'constants/routeNamesExternal'

/**
 * 2.0.0.8 MyATD - Dashboard - Membership
 * UX: https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=3528%3A41884
 *
 * endpoint: user/profile
 * response: UserProfileDTO
 * response namespaces: User/Membership
 *
 * level
 * cycleEndDate
 * cycleEndDate
 *
 */

const useStyles = makeStyles(() => ({
  title: {
    color: '#2e2d31',
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: '26px',
  },
  membershipLabel: {
    color: '#424140',
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: '22px',
    '& + small': {
      color: '#D70000',
      fontSize: '11px',
      lineHeight: '16px',
    },
  },
  textBase: {
    color: '#424140',
    fontSize: '16px',
    lineHeight: '22px',
    margin: '0',
  },
}))

const Membership = () => {
  /******************************************
   * HOOKS
   ******************************************/

  const classes = useStyles()

  /******************************************
   * GQL HOOKS
   ******************************************/

  const { loading: isLoading, error: isError, data } = useQuery<
    GetUserQuery,
    GetUserQueryVariables
  >(GET_USER, { fetchPolicy: 'cache-first' })

  /******************************************
   * RENDER
   ******************************************/

  if (isError || (!data && !isLoading)) {
    return <QueryError />
  }

  const currentMembership = data?.user?.membership?.currentMembership
  const memberShip = currentMembership ? (
    <Grid container>
      <Grid item xs={12}>
        <Typography className={classes.membershipLabel} component={'h3'}>
          {currentMembership.memberTypeLabel}
        </Typography>

        <Typography component={'small'}>
          <span>{strings.DASHBOARD.EFFECTIVE}: </span>
          <strong>
            {Helper.condensedDateFormatter(currentMembership?.initialBeginDate)}
          </strong>
          <span> - </span>
          <strong>
            {Helper.condensedDateFormatter(currentMembership?.cycleEndDate)}
          </strong>
        </Typography>
      </Grid>

      <Spacer size={12} />

      <Grid item xs={12}>
        <Typography className={classes.textBase} component={'p'}>
          {strings.DASHBOARD.MEMBERSHIP_WIDGET_DESCRIPTION}
        </Typography>
      </Grid>
    </Grid>
  ) : (
    <Grid container>
      <Grid item xs={12}>
        <Typography className={classes.title} component={'h2'}>
          {strings.DASHBOARD.MEMBERSHIP_WIDGET_TITLE}
        </Typography>
      </Grid>

      <Spacer size={16} />

      <Grid item xs={12}>
        <Typography className={classes.textBase} component={'p'}>
          {parse(strings.DASHBOARD.MEMBERSHIP_WIDGET_SUB)}
        </Typography>
      </Grid>

      <Spacer size={16} />

      <Grid item xs={12}>
        <Button
          fullWidth
          alignment={'center'}
          variant="secondary"
          href={RouteNamesExternal.CHECKOUT_MEMBERSHIP}
        >
          {strings.DASHBOARD.MEMBERSHIP_WIDGET_BUTTON}
        </Button>
      </Grid>
    </Grid>
  )

  return !isLoading ? (
    <Box
      topBorderHeight={8}
      variant="subModule"
      header={
        currentMembership
          ? {
              title: strings.DASHBOARD.MY_MEMBERSHIP,
            }
          : undefined
      }
      footer={
        currentMembership
          ? {
              link: {
                url: Helper.getPathFromRouteKey('MYATD_BENEFITS_CENTER'),
                label: strings.DASHBOARD.VISIT_BENEFITS_CENTER,
              },
            }
          : undefined
      }
    >
      {memberShip}
    </Box>
  ) : (
    <Box topBorderHeight={8} variant="subModule">
      <SkeletonLoader />
    </Box>
  )
}

export default Membership
